import {
  accountIdFormat,
  locationIdFormat,
  productIdFormat,
  transferSessionIdFormat,
} from "@redotech/redo-model/ids";
import {
  FlushPickStrategy,
  FlushPickStrategySchema,
  FlushReasonSchema,
} from "@redotech/redo-model/inventory/flush";
import * as z from "zod/v4";

// ─── shared types ────────────────────────────────────────────────

const FlushSessionIdSchema = transferSessionIdFormat.schema;

const ResolvedFlushProductSchema = z.object({
  productId: productIdFormat.schema,
  productTitle: z.string(),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
});

const FlushScanErrorSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("unknown_code") }),
  z.object({
    kind: z.literal("not_at_warehouse"),
    product: ResolvedFlushProductSchema,
  }),
]);

const FlushSessionTotalsSchema = z.object({
  scanCount: z.number().int(),
  skuCount: z.number().int(),
});

const FlushScanSchema = z.object({
  sltaId: z.string(),
  productTitle: z.string(),
  sku: z.string().nullable(),
  sourceBinName: z.string(),
  quantity: z.number().int(),
  scannedAt: z.date(),
});

const PickLineSchema = z.object({
  id: z.string(),
  productId: productIdFormat.schema,
  productTitle: z.string(),
  sku: z.string().nullable(),
  sourceBinName: z.string(),
  expectedQuantity: z.number().int(),
  pickedQuantity: z.number().int(),
});

// ─── endpoints ───────────────────────────────────────────────────

export const startFlushSession = {
  name: "startFlushSession" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    reason: FlushReasonSchema,
    note: z.string().max(500).nullish(),
    pickStrategy: FlushPickStrategySchema.default(FlushPickStrategy.AUTOMATIC),
    merchantAccountId: accountIdFormat.schema,
    // Age threshold (in days) for the slow-mover branch of AUTOMATIC.
    // Items whose location_inventory was created MORE than this many
    // days ago AND have never had a fulfillment_created /
    // fulfillment_order_created adjustment are seeded into the worklist
    // alongside dead SKUs. Null falls back to DEFAULT_SLOW_MOVER_WINDOW_DAYS.
    slowMoverWindowDays: z.number().int().positive().max(365).nullish(),
  }),
  output: z.object({ flushSessionId: FlushSessionIdSchema }),
};

export const scanFlushItem = {
  name: "scanFlushItem" as const,
  input: z.object({
    flushSessionId: FlushSessionIdSchema,
    code: z.string().min(1),
  }),
  output: z.object({
    resolvedProduct: ResolvedFlushProductSchema.nullable(),
    sourceBinName: z.string().nullable(),
    flushBinName: z.string(),
    totals: FlushSessionTotalsSchema,
    error: FlushScanErrorSchema.nullable(),
    matchedPickLineId: z.string().nullable(),
  }),
};

export const finalizeFlushSession = {
  name: "finalizeFlushSession" as const,
  input: z.object({ flushSessionId: FlushSessionIdSchema }),
  output: z.object({ completedAt: z.date() }),
};

export const cancelFlushSession = {
  name: "cancelFlushSession" as const,
  input: z.object({ flushSessionId: FlushSessionIdSchema }),
  output: z.object({ canceledAt: z.date(), reversedCount: z.number().int() }),
};

export const getFlushSession = {
  name: "getFlushSession" as const,
  input: z.object({ flushSessionId: FlushSessionIdSchema }),
  output: z.object({
    session: z.object({
      flushSessionId: FlushSessionIdSchema,
      locationId: locationIdFormat.schema,
      reason: FlushReasonSchema,
      pickStrategy: FlushPickStrategySchema,
      flushBinName: z.string(),
      note: z.string().nullable(),
      operatorName: z.string().nullable(),
      createdAt: z.date(),
      completedAt: z.date().nullable(),
      canceledAt: z.date().nullable(),
    }),
    scans: z.array(FlushScanSchema),
    pickLines: z.array(PickLineSchema),
    totals: FlushSessionTotalsSchema,
  }),
};

export const getActiveFlushSession = {
  name: "getActiveFlushSession" as const,
  input: z.object({ locationId: locationIdFormat.schema }),
  output: z.object({
    session: z
      .object({
        flushSessionId: FlushSessionIdSchema,
        reason: FlushReasonSchema,
        createdAt: z.date(),
      })
      .nullable(),
  }),
};

export const listFlushMerchants = {
  name: "listFlushMerchants" as const,
  input: z.object({}),
  output: z.object({
    merchants: z.array(
      z.object({ accountId: accountIdFormat.schema, name: z.string() }),
    ),
  }),
};
